import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Heading,
  Dialog,
  DialogContent,
  IconButton,
  toast
} from '@sparrowengg/twigs-react';
import {
  PlusIcon,
  SearchIcon,
  ChevronRightIcon,
  CopyIcon,
  CloseIcon
} from '@sparrowengg/twigs-react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs } from '@sparrowengg/twigs-react';
import Apps from 'features/apps';
import Webhook from 'features/webhook';
import ConfigPage from 'pages/config-page';
import AppsIcon from 'commons/icons/apps';
import IntegrateIcon from 'commons/icons/integrate';
import { SingleAppDetails, WebhookDetailsType } from '../types';
import { currentTabValues } from '../constants';

const MCP_CONFIG = {
  mcpServers: {
    'appnest-app-mcp': {
      command: 'npx',
      args: ['-y', '@sparrowengg/appnest-app-mcp'],
      transport: 'stdio'
    }
  }
};

const Dashboard = ({
  isConfigPage,
  setIsConfigPage
}: {
  isConfigPage: boolean;
  setIsConfigPage: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab =
    new URLSearchParams(location.search).get('tab') ?? currentTabValues.APPS;
  const [currentTab, setCurrentTab] = useState(defaultTab);
  const [webhookList, setWebhookList] = useState<Array<WebhookDetailsType>>([]);
  const [apps, setApps] = useState<Array<SingleAppDetails>>([]);
  const [inputSearch, setInputSearch] = useState('');
  const [showMcpModal, setShowMcpModal] = useState(false);

  const isAppsPage = currentTab.includes(currentTabValues.APPS);

  const handleConfigPage = (configPage: boolean) => {
    setIsConfigPage(configPage);
  };

  return (
    <Box css={{ padding: '$20 $40' }}>
      {isConfigPage ? (
        <ConfigPage />
      ) : (
        <>
          <Tabs defaultValue={currentTab}>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex gap="$2">
                <Flex
                  alignItems="center"
                  gap="$4"
                  onClick={() => {
                    setCurrentTab(currentTabValues.APPS);
                    setInputSearch('');
                  }}
                  css={{
                    background: isAppsPage
                      ? 'rgba(74, 156, 166, 0.08)'
                      : 'transparent',
                    borderRadius: '$lg',
                    padding: '$4',
                    cursor: 'pointer',
                    ...(isAppsPage && {
                      borderBottom: '$borderWidths$sm solid $primary400'
                    })
                  }}
                >
                  <AppsIcon
                    size={'20'}
                    color={isAppsPage ? '#4A9CA6' : '#6A6A6A'}
                  />
                  <Text
                    weight="medium"
                    size="sm"
                    css={{ color: isAppsPage ? '$primary600' : '$neutral900' }}
                  >
                    {' '}
                    Apps
                  </Text>
                </Flex>
                <Flex
                  alignItems="center"
                  gap="$4"
                  onClick={() => {
                    setCurrentTab(currentTabValues.WEBHOOK);
                    setInputSearch('');
                  }}
                  css={{
                    background: !isAppsPage
                      ? 'rgba(74, 156, 166, 0.08)'
                      : 'transparent',
                    borderRadius: '$lg',
                    padding: '$4',
                    cursor: 'pointer',
                    ...(!isAppsPage && {
                      borderBottom: '$borderWidths$sm solid $primary400'
                    })
                  }}
                >
                  <IntegrateIcon
                    size={'20'}
                    color={!isAppsPage ? '#4A9CA6' : '#6A6A6A'}
                  />
                  <Text
                    weight="medium"
                    size="sm"
                    css={{ color: !isAppsPage ? '$primary600' : '$neutral900' }}
                  >
                    {' '}
                    Global Webhooks
                  </Text>
                </Flex>
              </Flex>
              <Flex alignItems="center" gap="$8">
                <Input
                  placeholder="Search Applications"
                  size="lg"
                  value={inputSearch}
                  onChange={(e) => setInputSearch(e.currentTarget.value)}
                  css={{ width: 320 }}
                  leftIcon={<SearchIcon />}
                />
                {isAppsPage && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => setShowMcpModal(true)}
                  >
                    MCP Configuration
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/event-logs')}
                >
                  Event Logs
                </Button>
                <Button
                  color="primary"
                  size="lg"
                  rightIcon={<PlusIcon />}
                  onClick={() =>
                    isAppsPage
                      ? handleConfigPage(true)
                      : navigate('/add-webhook')
                  }
                >
                  {`New ${isAppsPage ? 'App' : 'Webhook'}`}
                </Button>
              </Flex>
            </Flex>
            {currentTab === currentTabValues.APPS ? (
              <Apps
                apps={apps}
                inputSearch={inputSearch}
                setApps={setApps}
                handleConfigPage={handleConfigPage}
              />
            ) : (
              <Webhook
                webhookList={webhookList}
                setWebhookList={setWebhookList}
                inputSearch={inputSearch}
              />
            )}
          </Tabs>
        </>
      )}
      {showMcpModal && (
        <McpConfigModal onClose={() => setShowMcpModal(false)} />
      )}
    </Box>
  );
};

const McpConfigModal = ({ onClose }: { onClose: () => void }) => {
  const configString = JSON.stringify(MCP_CONFIG, null, 2);
  const shellCommand = `mkdir -p .cursor && cat > .cursor/mcp.json << 'EOF'\n${configString}\nEOF`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shellCommand);
    toast({
      variant: 'success',
      description: 'Command copied to clipboard'
    });
  };

  return (
    <Dialog open>
      <DialogContent
        css={{ padding: 0, borderRadius: '$xl', width: 560, maxWidth: '90vw' }}
      >
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">MCP Configuration</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={onClose}
          />
        </Flex>
        <Flex flexDirection="column" gap="$6" css={{ padding: '$12' }}>
          <Text size="md" css={{ color: '$neutral800', lineHeight: '$md' }}>
            Copy and run this command in your project root to set up the MCP
            configuration
          </Text>
          <Box
            css={{
              position: 'relative',
              backgroundColor: '$neutral50',
              border: '$borderWidths$xs solid $neutral200',
              borderRadius: '$md',
              padding: '$8',
              fontFamily: 'monospace',
              fontSize: '$sm',
              overflow: 'auto',
              maxHeight: '300px',
              lineHeight: '$md'
            }}
          >
            <Box
              as="pre"
              css={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '$neutral900',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit'
              }}
            >
              {shellCommand}
            </Box>
            <IconButton
              icon={<CopyIcon />}
              size="sm"
              variant="ghost"
              color="default"
              onClick={handleCopy}
              css={{
                position: 'absolute',
                top: '$4',
                right: '$4',
                backgroundColor: '$white900',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                '&:hover': { backgroundColor: '$neutral100' }
              }}
            />
          </Box>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          css={{ padding: '$12', paddingTop: 0 }}
        >
          <Button
            size="md"
            variant="ghost"
            color="default"
            rightIcon={<ChevronRightIcon />}
            onClick={() =>
              window.open(
                'https://developers.salesparrow.com/appnest/appnest-app-mcp#cursor--mcp-config-example',
                '_blank'
              )
            }
          >
            Learn More
          </Button>
          <Flex alignItems="center" gap="$4">
            <Button size="md" variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button
              size="md"
              color="secondary"
              onClick={handleCopy}
              rightIcon={<CopyIcon />}
            >
              Copy Command
            </Button>
          </Flex>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default Dashboard;
